import { Page } from "./base";
import householdSetupTemplate from "../templates/pages/householdsetup.html?raw";
import { postApi } from "../services/api";
import { getElementById, reloadWindow } from "../services/dom";
import { clearAllDatabases } from "../services/indexeddb/clearDatabases";

export default class HouseholdSetupPage extends Page {
  private createForm: HTMLFormElement;
  private joinForm: HTMLFormElement;
  private createButton: HTMLButtonElement;
  private joinButton: HTMLButtonElement;

  constructor() {
    super(householdSetupTemplate);
    this.createForm = getElementById("createHouseholdForm") as HTMLFormElement;
    this.joinForm = getElementById("joinHouseholdForm") as HTMLFormElement;
    this.createButton = getElementById("createHouseholdButton") as HTMLButtonElement;
    this.joinButton = getElementById("joinHouseholdButton") as HTMLButtonElement;
    this.loadPage();
  }

  private loadPage(): void {
    // Toggle between create and join modes
    const createTabButton = getElementById("createTabButton");
    const joinTabButton = getElementById("joinTabButton");

    createTabButton.addEventListener("click", () => this.switchMode("create"));
    joinTabButton.addEventListener("click", () => this.switchMode("join"));

    // Logout button
    const logoutButton = getElementById("logoutButton");
    logoutButton.addEventListener("click", async () => {
      const confirmLogout = confirm("Are you sure you want to log out?");
      if (confirmLogout) {
        localStorage.clear();
        await clearAllDatabases();
        reloadWindow();
      }
    });

    // Form submissions
    this.createForm.addEventListener("submit", (e) => this.handleCreateHousehold(e));
    this.joinForm.addEventListener("submit", (e) => this.handleJoinHousehold(e));

    super.handleCustomAttributes();
  }

  private switchMode(mode: "create" | "join"): void {
    const createSection = getElementById("createSection");
    const joinSection = getElementById("joinSection");
    const createTabButton = getElementById("createTabButton");
    const joinTabButton = getElementById("joinTabButton");

    if (mode === "create") {
      createSection.classList.remove("hidden");
      joinSection.classList.add("hidden");
      createTabButton.classList.add("border-gray-400", "text-gray-400");
      createTabButton.classList.remove("border-transparent", "text-gray-400");
      joinTabButton.classList.remove("border-gray-400", "text-gray-400");
      joinTabButton.classList.add("border-transparent", "text-gray-400");
    } else {
      createSection.classList.add("hidden");
      joinSection.classList.remove("hidden");
      createTabButton.classList.remove("border-gray-400", "text-gray-400");
      createTabButton.classList.add("border-transparent", "text-gray-400");
      joinTabButton.classList.add("border-gray-400", "text-gray-400");
      joinTabButton.classList.remove("border-transparent");
    }
  }

  private async handleCreateHousehold(event: Event): Promise<void> {
    event.preventDefault();

    const formData = new FormData(this.createForm);
    const householdName = formData.get("householdName") as string;

    if (!householdName || householdName.trim() === "") {
      this.showError("create", "Please enter a household name");
      return;
    }

    // Disable button and show loading state
    this.createButton.disabled = true;
    this.createButton.textContent = "Creating...";
    this.createButton.classList.add("opacity-50", "cursor-not-allowed");

    try {
      const response = await postApi(
        this.apiUrl,
        "household",
        { name: householdName },
        this.apiToken
      );

      console.log("Household created:", response);
      this.showSuccess("create", "Household created successfully!");

      // Redirect to home after short delay
      setTimeout(() => {
        window.location.href = "/";
      }, 1500);
    } catch (error) {
      console.error("Error creating household:", error);
      this.showError("create", "Failed to create household. Please try again.");

      // Re-enable button
      this.createButton.disabled = false;
      this.createButton.textContent = "Create Household";
      this.createButton.classList.remove("opacity-50", "cursor-not-allowed");
    }
  }

  private async handleJoinHousehold(event: Event): Promise<void> {
    event.preventDefault();

    const formData = new FormData(this.joinForm);
    const inviteCode = formData.get("inviteCode") as string;

    if (!inviteCode || inviteCode.trim() === "") {
      this.showError("join", "Please enter an invite code");
      return;
    }

    // Disable button and show loading state
    this.joinButton.disabled = true;
    this.joinButton.textContent = "Joining...";
    this.joinButton.classList.add("opacity-50", "cursor-not-allowed");

    try {
      const response = await postApi(
        this.apiUrl,
        "household/join",
        { invite_code: inviteCode.trim().toUpperCase() },
        this.apiToken
      );

      const data = JSON.parse(response as string);
      this.showSuccess("join", `Successfully joined ${data.message.household.name}!`);

      // Redirect to home after short delay
      setTimeout(() => {
        window.location.href = "/";
      }, 1500);
    } catch (error: any) {
      console.error("Error joining household:", error);

      let errorMessage = "Failed to join household. Please check your invite code.";
      if (error.status === 404 || error.status === 410) {
        errorMessage = "Invalid or expired invite code.";
      } else if (error.status === 400) {
        errorMessage = "You are already a member of a household.";
      }

      this.showError("join", errorMessage);

      // Re-enable button
      this.joinButton.disabled = false;
      this.joinButton.textContent = "Join Household";
      this.joinButton.classList.remove("opacity-50", "cursor-not-allowed");
    }
  }

  private showError(formType: "create" | "join", message: string): void {
    const errorDivId = formType === "create" ? "createErrorMessage" : "joinErrorMessage";
    const errorDiv = getElementById(errorDivId);
    errorDiv.innerHTML = `
      <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm">
        ${message}
      </div>
    `;
  }

  private showSuccess(formType: "create" | "join", message: string): void {
    const errorDivId = formType === "create" ? "createErrorMessage" : "joinErrorMessage";
    const errorDiv = getElementById(errorDivId);
    errorDiv.innerHTML = `
      <div class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded text-sm">
        ${message}
      </div>
    `;
  }
}
